import React from 'react';
import { Link } from 'react-router-dom';
import { FiCheckCircle, FiTrash2 } from 'react-icons/fi';
import { useSession } from '@/hooks/useSession';
import { confirm } from '@/lib/confirm';
import { PlayfulCard } from '@/components/custom/playful-card';
import { Badge } from '@/components/ui/badge';
import essayIcon from '@/assets/images/icons/essay.png';

const BADGE_CLASSNAME = 'rounded-full px-2.5 py-1 text-xs font-bold';

const DIFFICULTY_VARIANT = {
  fácil: 'success-soft',
  média: 'amber-soft',
  difícil: 'danger-soft'
};

export function ExercisesCardPlayful({ book, onDelete }) {
  const { session: userSession } = useSession();
  const difficultyLabel = book['t-difficulty'];
  const difficultyVariant = DIFFICULTY_VARIANT[difficultyLabel] || 'success-soft';
  const isRequired = book?.['learning-system-question-books']?.[0]?.is_required === true;
  const taxonomies = book.taxonomies || [];
  const questionCount = book['question-count'] || 0;
  const questionCountLabel = questionCount === 1 ? '1 questão' : `${questionCount} questões`;
  const isOwner = book?.creator?.id === userSession?.user?.id;

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Deseja mesmo excluir este exercício?',
      description: 'Esta ação não poderá ser desfeita',
      confirmText: 'Sim',
      cancelText: 'Não'
    });
    if (confirmed) {
      onDelete(book.id);
    }
  };

  return (
    <PlayfulCard className="p-6">
      <div className="relative mb-5 flex items-start justify-between">
        <img
          src={essayIcon}
          alt=""
          className="h-20 w-20 object-contain"
        />
        <div className="flex items-center gap-1.5">
          {isRequired && (
            <Badge
              variant="danger"
              className={BADGE_CLASSNAME}
            >
              Obrigatório
            </Badge>
          )}
          <Badge
            variant={difficultyVariant}
            className={BADGE_CLASSNAME}
          >
            {difficultyLabel}
          </Badge>
          {isOwner && (
            <button
              type="button"
              aria-label="Excluir caderno"
              onClick={handleDelete}
              className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-danger-light hover:text-danger"
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <h3
        className="text-lg font-bold"
        style={{ color: '#222731' }}
      >
        {book.title}
      </h3>

      {taxonomies.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {taxonomies.slice(0, 2).map(tax => (
            <span
              key={tax.id}
              className="rounded-full bg-[var(--color-bg-teal-soft)] px-2.5 py-0.5 text-xs font-semibold text-[var(--color-primary)]"
            >
              {tax.name}
            </span>
          ))}
          {taxonomies.length > 2 && <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-semibold text-neutral-400">+{taxonomies.length - 2}</span>}
        </div>
      )}

      <div className="mb-6 mt-4 flex flex-1 items-center gap-1.5 text-sm font-semibold text-neutral-500">
        <FiCheckCircle className="h-4 w-4 text-[var(--color-mission-medium)]" />
        {questionCountLabel}
      </div>

      <Link
        to={`/questions/apostilas/${book.id}`}
        className="group/btn relative block w-full overflow-hidden rounded-full py-3 text-center text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
        style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 4px 0 var(--color-mission-dark), 0 6px 12px rgba(245,123,46,0.35)' }}
      >
        Responder
      </Link>
    </PlayfulCard>
  );
}
